import { v4 as uuidv4 } from "uuid";
import propertyOwnershipMap from "../../data/property_ownership_mapping";
import { getLoggedStatus, getLoggedUserInfo } from "../helpers";
import { Hotel, Product, Room, Search } from "../../@types/IDatalayers";
import { sha256 } from "./sha256";
import { differenceInDays } from "date-fns";

export const addPurchaseEvent = async (
  confirmedReservations,
  hotel,
  brand,
  details,
  staticBooking,
  search,
  resType
): Promise<void> => {
  const brandName =
    hotel.relationships &&
    hotel.relationships.brand_id &&
    hotel.relationships.brand_id.name;
  const totalRooms = search?.rooms?.length;
  const checkinDate = new Date(search.checkin);
  const checkoutDate = new Date(search.checkout);
  const products: Product[] = [];
  const lengthOfStay = Math.ceil(
    (checkoutDate - checkinDate) / (1000 * 60 * 60 * 24)
  );
  const { email, profileType } = getLoggedUserInfo();
  const totalAdults = search.rooms.reduce((sum, r) => sum + r.adults, 0);
  const totalChildren = search.rooms.reduce((sum, r) => sum + r.children, 0);
  const totalResGuests = totalAdults + totalChildren;

  const loggedState = getLoggedStatus();

  const resEmail =
    confirmedReservations?.[0]?.details?.guests?.[0]?.email ?? "";

  const emailId = details?.resEmail || email || resEmail;

  let emailHash = "";
  if (emailId) {
    emailHash = await sha256(emailId);
  }

  const prevRoomDetails = window._roomDataLayerStore;

  const firstReservation =
    confirmedReservations && confirmedReservations.length
      ? confirmedReservations[0]
      : null;

  const resDetail = confirmedReservations[0].details;
  const firstRoomRate =
    resDetail.roomRates && resDetail.roomRates.length > 0
      ? resDetail.roomRates[0]
      : null;
  const address = resDetail.hotelAddress;

  const numNights = differenceInDays(
    new Date(resDetail.End),
    new Date(resDetail.Start)
  );
  let roomTotal = 0,
    totalTax = 0,
    totalUpgrades = 0,
    totalFees = 0;

  let adults = 0;
  let children = 0;
  let totalGuests = 0;
  confirmedReservations.forEach((r, rIndex) => {
    const rDetail = r.details;
    const payment = {
      Subtotal: 0,
      Upgrades: 0,
      Taxes: 0,
      Total: 0,
      Fees: 0,
    };
    const roomRate =
      rDetail.roomRates && rDetail.roomRates.length > 0
        ? rDetail.roomRates[0]
        : null;
    rDetail.roomRates.forEach((item: any) => {
      payment.Subtotal += item.subTotal
        ? parseFloat(item.subTotal)
        : item.Subtotal
        ? parseFloat(item.Subtotal)
        : 0;
      payment.Upgrades += item.upgrades
        ? parseFloat(item.upgrades)
        : item.Upgrades
        ? parseFloat(item.Upgrades)
        : 0;
      payment.Taxes += item.taxes
        ? parseFloat(item.taxes)
        : item.Taxes
        ? parseFloat(item.Taxes)
        : 0;
      payment.Total += item.total
        ? parseFloat(item.total)
        : item.Total
        ? parseFloat(item.Total)
        : 0;
      payment.Fees += item.fees ? parseFloat(item.fees) : 0;
    });

    adults = adults + +r.details.count.adult;
    if (r.details.count.child) {
      children = children + +r.details.count.child;
    }
    totalGuests = adults + children;

    roomTotal += parseFloat(payment.Subtotal);
    totalTax += parseFloat(payment.Taxes);
    totalUpgrades += parseFloat(payment.Upgrades);
    totalFees += parseFloat(payment.Fees);

    const product: any = {};

    product.item_transaction_id = r.crs_reservation_id; // Individual Transaction ID of room
    product.transaction_type = "new";
    product.item_id = rDetail.roomTypeCode; // ID of the room
    product.item_name = rDetail.roomDescription && rDetail.roomDescription.name; // Name of the room
    product.index = rIndex + 1; //index/position of the item in a list
    product.quantity = numNights; // number of nights booked
    product.coupon = search?.promotionCode || search?.groupCode || ""; // The coupon name/code associated with the item
    product.price = roomRate?.roomRateWithFees?.toFixed(2); // monetary value of the room Room Price + Fees
    product.add_on = roomRate?.upgrades
      ? parseFloat(roomRate.upgrades).toFixed(2)
      : 0; // Add-on value
    product.fee = roomRate?.fees ? parseFloat(roomRate.fess).toFixed(2) : 0;
    product.currency = roomRate?.currencyCode; // Currency associated with the event.
    product.item_hotel_brand = brandName; //Hotel brand
    product.item_hotel_name = hotel.name; // Hotel Name
    product.item_hotel_id = resDetail.hotel.id; // Hotel ID
    product.item_variant = resDetail?.ratePlanName; //Selected rate on checkout
    product.item_variant_id = resDetail?.ratePlanCode; // id of selected rate on checkout
    product.item_list_id = "Product Listing"; //
    product.item_category = "Room"; // Room type
    product.item_list_name =
      resType === "update"
        ? "Select Room"
        : prevRoomDetails
        ? prevRoomDetails[rIndex]?.item_list_name
        : ""; // Expected value "Hotel Details" or "Select Room"
    product.Start_date = resDetail?.start; // Start day of booking.
    product.End_date = resDetail?.end; // End day of booking.
    product.adults = resDetail?.count?.adult || 0; // Number of adults
    product.children = resDetail?.count?.child || 0; // Number of children
    product.total_guest =
      (resDetail?.count?.adult || 0) + (resDetail?.count?.child || 0); // Total number of guests
    product.ratecount = details?.rateCount; // No. of rates displayed
    product.selectedratetype = details?.isonlyRate
      ? "Only Rate Displayed"
      : details?.lowestRate === details?.rateCode
      ? "Lowest Rate"
      : "Not the Lowest Rate"; // Possible values "Lowest Rate","Not the Lowest Rate", and "Only Rate Displayed"

    products.push(product);
  });

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: "purchase",
    ecommerce: {
      event_id: uuidv4(),
      transaction_type: resType,
      transaction_id:
        confirmedReservations && confirmedReservations.length
          ? confirmedReservations[0].crs_reservation_id
          : "",
      rooms:
        confirmedReservations && confirmedReservations.length
          ? confirmedReservations.length
          : 0,
      value: (roomTotal + totalUpgrades).toFixed(2),
      revenue: (roomTotal + totalUpgrades).toFixed(2),
      tax: totalTax.toFixed(2),
      add_on: totalUpgrades.toFixed(2),
      fee: (totalFees || 0).toFixed(2),
      currency: firstRoomRate?.currencyCode,
      coupon: search?.promotionCode || search?.groupCode || "",
      rate_code: details?.rateCode,
      rate_name: details?.rateName,
      hotel_brand: brandName,
      hotel_name: resDetail?.hotel?.name,
      hotel_id: resDetail?.hotel?.id,
      check_in_date: resDetail?.start,
      check_out_date: resDetail?.end,
      adults: totalAdults,
      children: totalChildren,
      total_guest: totalResGuests,
      length_of_stay: lengthOfStay,
      affiliation: propertyOwnershipMap[hotel?.hotel_code],
      city: hotel?.address?.locality,
      list_id: "Product Listing",
      category: "Room",
      list_name:
        resType === "update"
          ? "Select Room"
          : window && window._purchaseStartPage
          ? window._purchaseStartPage
          : "",
      loggedState: loggedState,
      emailHash: emailHash,
      profileType: profileType,
      email: emailId,
      items: products,
    },
  });
};
